import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import MyButton from "../../../components/MyButton";
import { useBoardItem } from "../../../hooks/useBoardItem";
import { AuthContext } from "../../../context/AuthProvider";
import { PopupContext } from "../../../context/PopupProvider";
import DeleteBoardItemDialog from "../../Common/DeleteBoardItemDialog";
import MyLoading from "../../../components/MyLoading";
import { getStatusLabel, renderBody } from "../../../utils";
import MyTitle from "../../../components/MyTitle";
import EnterWriterPasswordDialog from "../../Common/EnterWriterPasswordDialog";
import { SiteContext } from "../../../context/SiteProvider";
import Answer from "../../Answer";
import FileItem from "../../../components/inputs/MyInputFile/FileItem";
import {
  BOARD_PERMISSION,
  NON_MEMBER_PERMISSIONS,
} from "../../../constants/permission";
import {
  CheckRoleAndPermissionDisplay,
  checkRoleAndPermissions,
  getPermissionMessage,
} from "../../../components/CheckRoleAndPermission";

const FamilyDetail06 = ({ boardCode }) => {
  const { itemNo } = useParams();
  const {
    useGetBoardItem,
    useGetBoard,
    deleteBoardItemMutation,
    verifyPasswordEditBoardMutation,
  } = useBoardItem();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, profile, roleCode } =
    useContext(AuthContext);
  const { openPopup } = useContext(PopupContext);
  const { setLoadingScreen } = useContext(SiteContext);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const nonPermissions = NON_MEMBER_PERMISSIONS[boardCode];

  const privateViewToken = new URLSearchParams(window.location.search).get(
    "privateViewToken"
  );

  const [replyMode, setReplyMode] = useState(false);

  const { data: board, isLoading: isBoardLoading } = useGetBoard(
    { boardCode },
    (data) => data.data
  );

  const { data, isLoading, isError, error } = useGetBoardItem(
    {
      boardCode: board?.boardCode,
      itemNo,
      includeAdjacent: true,
      privateViewToken,
      enabled: checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      ),
    },
    (data) => data.data
  );

  const openDeleteDialog = () =>
    openPopup(
      <DeleteBoardItemDialog
        boardCode={board?.boardCode}
        itemNo={itemNo}
        onRedirect={() => {
          navigate(`/Family/Curious`);
        }}
      />
    );

  const openPopupGoToEditPageOfNonMember = () => {
    isAdmin
      ? navigate(`/Family/Curious/Edit/${itemNo}`)
      : openPopup(
          <EnterWriterPasswordDialog
            params={{ boardCode: board?.boardCode, itemNo }}
            permission="edit"
            onAction={(res) =>
              navigate(
                `/Family/Curious/Edit/${itemNo}?privateViewToken=${res.privateViewToken}&anonymousEditToken=${res.anonymousEditToken}`
              )
            }
            mutation={verifyPasswordEditBoardMutation}
          />
        );
  };

  const openPopupDeleteOfNonMember = () => {
    isAdmin
      ? openDeleteDialog()
      : openPopup(
          <EnterWriterPasswordDialog
            params={{ boardCode: board?.boardCode, itemNo }}
            permission="delete"
            onAction={() => {
              setLoadingScreen(true);
              deleteBoardItemMutation.mutate(
                { boardCode, itemNo },
                {
                  onSuccess: () => {
                    alert(t("common.messages.boardItem.deleteSuccess"));
                    queryClient.invalidateQueries({
                      queryKey: ["/board-item?boardCode={boardCode}"],
                    });
                    navigate(`/Family/Curious`);
                  },
                  onError: () => {
                    alert(t("common.messages.boardItem.error"));
                  },
                  onSettled: () => {
                    setLoadingScreen(false);
                  },
                }
              );
            }}
            mutation={verifyPasswordEditBoardMutation}
          />
        );
  };

  useEffect(() => {
    if (
      !isBoardLoading &&
      !!board &&
      !checkRoleAndPermissions(
        roleCode,
        board,
        BOARD_PERMISSION.READ,
        nonPermissions,
        isAdmin
      )
    )
      alert(getPermissionMessage(BOARD_PERMISSION.READ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board, isBoardLoading, roleCode, isAdmin]);

  return (
    <CheckRoleAndPermissionDisplay
      showMessage={!isBoardLoading && !!board}
      board={board}
      permission={BOARD_PERMISSION.READ}
      boardCode={boardCode}
    >
      {isLoading ? (
        <MyLoading className="h-[150px]" />
      ) : (
        <>
          <MyTitle title="내용보기" className="mb-4" />

          <div className="border-t-2 border-primary overflow-x-auto max-w-full">
            {isError && !data ? (
              <p className="text-center text-lg text-gray-dark pt-10">
                {error?.data?.code === "NO_PERMISSION"
                  ? "이 글은 비밀글입니다. 작성자 및 관리자만 열람하실 수 있습니다."
                  : "이 게시물의 정보를 볼 수 없습니다."}
              </p>
            ) : (
              <>
                <table className="w-full border-collapse mb-5">
                  <tbody>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        제목
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {data?.title || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        작성자
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {data?.writer?.name || "-"}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        작성일
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {dayjs(data?.createdDate).format("YYYY-MM-DD")}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        진행상태
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {getStatusLabel(
                          data?.dateOfFirstComment ? "replied" : "waiting",
                          "2xl:text-base text-sm"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        내용
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {data?.body ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: renderBody(data.body, data.embeddedFiles),
                            }}
                            className="body-text"
                          />
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        첨부파일
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {data?.attachedFiles?.length > 0 ? (
                          <div>
                            {data.attachedFiles.map((item) => (
                              <FileItem key={item.fileId} file={item} />
                            ))}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="text-left p-4 font-semibold bg-gray-100 border border-gray-border 2xl:text-base text-sm md:w-[25%] w-[100px]">
                        동영상
                      </th>
                      <td className="text-left p-4 border border-gray-border text-gray-dark 2xl:text-base text-sm w-auto">
                        {data?.additionalItems?.videoUrl ? (
                          <iframe
                            height={315}
                            width={560}
                            title="Video Player"
                            src={data.additionalItems.videoUrl}
                            frameBorder="0"
                            allow="accelerometer; autoplay; fullscreen; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-auto bg-black"
                            style={{
                              aspectRatio: 16 / 9,
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div className="flex items-center mt-10 sm:gap-4 gap-2">
                  {checkRoleAndPermissions(
                    roleCode,
                    board,
                    BOARD_PERMISSION.ACCESS,
                    nonPermissions,
                    isAdmin
                  ) && (
                    <MyButton
                      variant="contained"
                      color="#111111"
                      className="font-semibold sm:min-w-[120px] mr-auto text-white"
                      onClick={() => navigate(`/Family/Curious`)}
                    >
                      목록보기
                    </MyButton>
                  )}

                  {checkRoleAndPermissions(
                    roleCode,
                    board,
                    BOARD_PERMISSION.WRITE,
                    nonPermissions,
                    isAdmin
                  ) && (
                    <>
                      {isAuthenticated &&
                      ((profile?.userId === data?.writer?.userId &&
                        !data?.dateOfFirstComment) ||
                        isAdmin) ? (
                        <>
                          <MyButton
                            variant="outlined"
                            className="sm:min-w-[120px]"
                            onClick={openDeleteDialog}
                          >
                            <span className="font-semibold">삭제</span>
                          </MyButton>

                          <MyButton
                            variant="outlined"
                            className="sm:min-w-[120px]"
                            onClick={() =>
                              navigate(`/Family/Curious/Edit/${itemNo}`)
                            }
                          >
                            <span className="font-semibold">수정하기</span>
                          </MyButton>
                        </>
                      ) : !isAuthenticated && !data?.dateOfFirstComment ? (
                        <>
                          <MyButton
                            variant="outlined"
                            className="sm:min-w-[120px]"
                            onClick={openPopupDeleteOfNonMember}
                          >
                            <span className="font-semibold">삭제</span>
                          </MyButton>
                          <MyButton
                            variant="outlined"
                            className="sm:min-w-[120px]"
                            onClick={openPopupGoToEditPageOfNonMember}
                          >
                            <span className="font-semibold">수정하기</span>
                          </MyButton>
                        </>
                      ) : null}
                    </>
                  )}

                  {!!board?.options?.useComment &&
                  isAdmin &&
                  !replyMode &&
                  !data?.dateOfFirstComment ? (
                    <MyButton
                      variant="contained"
                      color="primary"
                      className="sm:min-w-[120px] font-semibold"
                      onClick={() => {
                        setReplyMode(true);
                        const form = document.getElementById("reply-form");

                        if (form) {
                          form.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }
                      }}
                    >
                      답변하기
                    </MyButton>
                  ) : (
                    <></>
                  )}
                </div>

                <Answer
                  board={board}
                  boardItem={data}
                  parent="/Family/Curious"
                  className="mt-10"
                  replyMode={replyMode}
                  setReplyMode={setReplyMode}
                />
              </>
            )}
          </div>
        </>
      )}
    </CheckRoleAndPermissionDisplay>
  );
};

export default FamilyDetail06;
